// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
// Cleaning Session Protocol — Zone checklist + photo proof artifacts
// "Every zone cleaned is a zone documented."
// Janitor completes tasks + photos → AWAITING_APPROVAL → Supervisor approves
// ================================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import {
  Camera, CheckCircle, X, AlertTriangle, Send, ArrowLeft,
  ClipboardList, ImagePlus, Shield, Clock
} from 'lucide-react';

export default function CleaningSessionPage() {
  const { zoneId } = useParams();
  const { user }   = useAuth();
  const navigate   = useNavigate();

  const [zone, setZone]           = useState(null);
  const [tasks, setTasks]         = useState([]);
  const [session, setSession]     = useState(null);
  const [checks, setChecks]       = useState({});       // taskId → boolean
  const [photos, setPhotos]       = useState({});       // proofType → { file, preview, url }
  const [notes, setNotes]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [phase, setPhase]         = useState('checklist'); // checklist | photo | submit
  const fileInputRef              = useRef(null);
  const [activePhotoType, setActivePhotoType] = useState(null);

  useEffect(() => {
    loadZoneData();
  }, [zoneId]);

  const loadZoneData = async () => {
    const [{ data: z }, { data: t }, { data: store }] = await Promise.all([
      supabase.from('grocery_zones').select('*').eq('id', zoneId).single(),
      supabase.from('grocery_cleaning_tasks').select('*').eq('zone_id', zoneId).eq('active', true).order('task_order'),
      supabase.from('grocery_stores').select('id').eq('active', true).limit(1).single(),
    ]);

    setZone(z);
    setTasks(t || []);

    // Find or create a session for today
    const today = new Date().toISOString().split('T')[0];
    const { data: existing } = await supabase
      .from('grocery_cleaning_sessions')
      .select('*')
      .eq('zone_id', zoneId)
      .gte('created_at', today)
      .in('session_status', ['PENDING', 'IN_PROGRESS', 'COMPLETED'])
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (existing) {
      setSession(existing);
    } else {
      // Create new session
      const { data: newSess } = await supabase
        .from('grocery_cleaning_sessions')
        .insert({
          store_id: store?.id,
          zone_id: zoneId,
          assigned_to: user?.auth_user_id || user?.id,
          session_status: 'IN_PROGRESS',
          started_at: new Date().toISOString(),
        })
        .select()
        .single();
      setSession(newSess);
    }

    setLoading(false);
  };

  const toggleCheck = (taskId) => {
    setChecks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const allTasksDone  = tasks.every(t => checks[t.id]);
  const requiredPhotos = ['zone_before', 'zone_after'];
  const hasBeforePhoto = !!photos['zone_before'];
  const hasAfterPhoto  = !!photos['zone_after'];
  const canSubmit      = allTasksDone && hasAfterPhoto;

  const handlePhotoCapture = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !activePhotoType) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotos(prev => ({ ...prev, [activePhotoType]: { file, preview: ev.target.result, url: null } }));
    };
    reader.readAsDataURL(file);
  }, [activePhotoType]);

  const openCamera = (type) => {
    setActivePhotoType(type);
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const uploadPhoto = async (type, file) => {
    const ext      = file.name.split('.').pop();
    const fileName = `grocery-cleaning/${session.id}/${type}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from('proof-photos')
      .upload(fileName, file, { contentType: file.type });

    if (error) return null;
    const { data } = supabase.storage.from('proof-photos').getPublicUrl(fileName);
    return data?.publicUrl || null;
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting || !session) return;
    setSubmitting(true);

    try {
      // 1. Upload photos and insert proof artifacts
      for (const [type, photoData] of Object.entries(photos)) {
        if (!photoData?.file) continue;
        const url = await uploadPhoto(type, photoData.file);
        await supabase.from('grocery_proof_artifacts').insert({
          session_id:      session.id,
          zone_id:         zoneId,
          proof_type:      type,
          storage_url:     url,
          captured_at:     new Date().toISOString(),
          captured_by:     user?.auth_user_id || user?.id,
          file_size_bytes: photoData.file.size,
          metadata: {
            zone_name:  zone?.zone_name,
            tasks_done: Object.keys(checks).filter(k => checks[k]).length,
            total_tasks: tasks.length,
          },
        });
      }

      // 2. Insert checklist entries
      const entries = tasks.map(t => ({
        session_id:   session.id,
        task_id:      t.id,
        completed:    !!checks[t.id],
        completed_at: checks[t.id] ? new Date().toISOString() : null,
        completed_by: checks[t.id] ? (user?.auth_user_id || user?.id) : null,
      }));
      if (entries.length > 0) {
        await supabase.from('grocery_checklist_entries').insert(entries);
      }

      // 3. Calculate score
      const doneTasks   = tasks.filter(t => checks[t.id]).length;
      const score       = tasks.length > 0 ? Math.round((doneTasks / tasks.length) * 100) : 100;

      // 4. Transition session status
      const nextStatus = zone?.requires_supervisor_sign ? 'AWAITING_APPROVAL' : 'APPROVED';
      await supabase.from('grocery_cleaning_sessions').update({
        session_status: nextStatus,
        completed_at:   new Date().toISOString(),
        supervisor_notes: notes || null,
        score_pct:      score,
        updated_at:     new Date().toISOString(),
      }).eq('id', session.id);

      // 5. If requires supervisor — insert approval request
      if (zone?.requires_supervisor_sign) {
        await supabase.from('grocery_supervisor_approvals').insert({
          session_id:   session.id,
          requested_at: new Date().toISOString(),
          requested_by: user?.auth_user_id || user?.id,
        });
      }

      setPhase('done');
    } catch (err) {
      console.error('Session submission failed:', err);
      alert('Failed to submit session. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!zone)   return <div className="page-body" style={{ textAlign: 'center', color: '#94A3B8' }}>Zone not found.</div>;

  const completedCount = tasks.filter(t => checks[t.id]).length;

  // ── DONE screen ──────────────────────────────────────────────────
  if (phase === 'done') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F0FDF9', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 380 }}>
          <div style={{ fontSize: 80, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#0F766E', marginBottom: 8 }}>
            Session Submitted!
          </div>
          <div style={{ fontSize: 15, color: '#64748B', marginBottom: 6 }}>
            {zone.zone_name} cleaning session has been recorded.
          </div>
          {zone.requires_supervisor_sign ? (
            <div style={{ background: '#EDE9FE', color: '#7C3AED', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, marginBottom: 24 }}>
              <Shield size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Awaiting supervisor approval
            </div>
          ) : (
            <div style={{ background: '#D1FAE5', color: '#059669', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, marginBottom: 24 }}>
              <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Auto-approved — zone marked clean
            </div>
          )}
          <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={() => navigate('/zones')}>
            Back to Zone Map
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate('/zones')}
          >
            <ArrowLeft size={16} />
          </button>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: zone.zone_color + '20',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
          }}>
            {zone.zone_icon || '🧹'}
          </div>
          <div>
            <h1 className="page-title">{zone.zone_name}</h1>
            <p className="page-subtitle">
              Cleaning session · {completedCount}/{tasks.length} tasks done
              {zone.requires_supervisor_sign && ' · Supervisor sign-off required'}
            </p>
          </div>
        </div>

        {/* Progress indicator */}
        <div style={{ display: 'flex', gap: 8 }}>
          {['checklist', 'photo', 'submit'].map((p, i) => (
            <div
              key={p}
              onClick={() => {
                if (p === 'photo' && allTasksDone) setPhase('photo');
                if (p === 'submit' && allTasksDone && hasAfterPhoto) setPhase('submit');
                if (p === 'checklist') setPhase('checklist');
              }}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: phase === p ? '#0F766E' : '#CCFBF1',
                color: phase === p ? '#fff' : '#0F766E',
              }}
            >
              {i + 1}. {p.charAt(0).toUpperCase() + p.slice(1)}
            </div>
          ))}
        </div>
      </div>

      <div className="page-body" style={{ maxWidth: 700, margin: '0 auto' }}>

        {/* ── PHASE 1: Checklist ── */}
        {phase === 'checklist' && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <ClipboardList size={20} color="#0F766E" />
              <span style={{ fontSize: 16, fontWeight: 700, color: '#134E4A' }}>Task Checklist</span>
              <span style={{
                marginLeft: 'auto', fontSize: 13, fontWeight: 700,
                color: allTasksDone ? '#10B981' : '#64748B',
              }}>
                {completedCount}/{tasks.length}
              </span>
            </div>

            {tasks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#94A3B8' }}>
                No tasks defined for this zone yet.
              </div>
            ) : (
              tasks.map(task => {
                const done = !!checks[task.id];
                return (
                  <button
                    key={task.id}
                    onClick={() => toggleCheck(task.id)}
                    style={{
                      width: '100%', padding: '14px 16px', marginBottom: 8,
                      border: `1px solid ${done ? '#A7F3D0' : '#E2E8F0'}`,
                      borderRadius: 12,
                      background: done ? '#ECFDF5' : '#fff',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14,
                      textAlign: 'left', transition: 'all 0.15s',
                    }}
                  >
                    <div style={{
                      width: 24, height: 24, borderRadius: 7, flexShrink: 0,
                      border: `2px solid ${done ? '#10B981' : '#CBD5E1'}`,
                      background: done ? '#10B981' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}>
                      {done && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: done ? '#065F46' : '#134E4A' }}>{task.task_name}</div>
                      {task.task_description && (
                        <div style={{ fontSize: 12, color: done ? '#059669' : '#94A3B8', marginTop: 2 }}>{task.task_description}</div>
                      )}
                    </div>
                    {task.requires_photo && (
                      <Camera size={16} color="#8B5CF6" style={{ flexShrink: 0 }} />
                    )}
                  </button>
                );
              })
            )}

            <div style={{ marginTop: 20 }}>
              <button
                className="btn btn-primary btn-lg"
                style={{ width: '100%' }}
                disabled={!allTasksDone}
                onClick={() => setPhase('photo')}
              >
                {allTasksDone ? (
                  <><Camera size={20} /> Continue to Photo Documentation</>
                ) : (
                  `Complete all ${tasks.length - completedCount} remaining tasks`
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── PHASE 2: Photo Documentation ── */}
        {phase === 'photo' && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <ImagePlus size={20} color="#0F766E" />
              <span style={{ fontSize: 16, fontWeight: 700, color: '#134E4A' }}>Photo Documentation</span>
            </div>
            <div style={{ fontSize: 13, color: '#64748B', marginBottom: 20 }}>
              Capture photo evidence of zone condition. "After" photo is required.
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handlePhotoCapture}
            />

            {/* Photo slots */}
            {[
              { type: 'zone_before', label: 'Before Cleaning', required: false, color: '#F59E0B', icon: '📸' },
              { type: 'zone_after',  label: 'After Cleaning',  required: true,  color: '#10B981', icon: '✅' },
            ].map(({ type, label, required, color, icon }) => (
              <div key={type} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#134E4A' }}>{label}</span>
                  {required && (
                    <span style={{ fontSize: 10, background: '#FEE2E2', color: '#DC2626', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
                      REQUIRED
                    </span>
                  )}
                </div>

                {photos[type] ? (
                  <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden' }}>
                    <img
                      src={photos[type].preview}
                      alt={label}
                      style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                    />
                    <button
                      onClick={() => setPhotos(prev => { const n = { ...prev }; delete n[type]; return n; })}
                      style={{
                        position: 'absolute', top: 8, right: 8,
                        background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 8,
                        padding: 6, cursor: 'pointer', display: 'flex', color: '#fff',
                      }}
                    >
                      <X size={16} />
                    </button>
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
                      padding: '16px 12px 10px',
                      display: 'flex', alignItems: 'center', gap: 6, color: '#fff', fontSize: 12, fontWeight: 600,
                    }}>
                      <CheckCircle size={14} color="#10B981" />
                      Photo captured
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openCamera(type)}
                    style={{
                      width: '100%', padding: '28px 16px',
                      border: `2px dashed ${color}50`,
                      borderRadius: 12, background: color + '08',
                      cursor: 'pointer', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 8, transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = color + '15'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = color + '08'; }}
                  >
                    <Camera size={32} color={color} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: color }}>Capture {label}</span>
                  </button>
                )}
              </div>
            ))}

            {/* Notes */}
            <div style={{ marginTop: 8, marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>
                Supervisor Notes (optional)
              </label>
              <textarea
                className="input"
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any issues found, extra work done, special circumstances..."
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setPhase('checklist')}>
                <ArrowLeft size={16} />
                Back
              </button>
              <button
                className="btn btn-primary btn-lg"
                style={{ flex: 1 }}
                disabled={!hasAfterPhoto}
                onClick={() => setPhase('submit')}
              >
                {hasAfterPhoto ? (
                  <><CheckCircle size={20} /> Review &amp; Submit</>
                ) : (
                  'Take After Photo to Continue'
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── PHASE 3: Review & Submit ── */}
        {phase === 'submit' && (
          <div className="card">
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🏁</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#134E4A' }}>Review &amp; Submit</div>
              <div style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>
                Confirm your cleaning session for {zone.zone_name}
              </div>
            </div>

            {/* Summary */}
            <div style={{ background: '#F0FDF9', borderRadius: 12, padding: 16, marginBottom: 20 }}>
              {[
                { label: 'Zone',        value: zone.zone_name },
                { label: 'Tasks Done',  value: `${completedCount}/${tasks.length}` },
                { label: 'Before Photo', value: photos['zone_before'] ? '✓ Captured' : '✗ Skipped' },
                { label: 'After Photo',  value: photos['zone_after']  ? '✓ Captured' : '✗ Missing' },
                { label: 'Approval',     value: zone.requires_supervisor_sign ? 'Supervisor required' : 'Auto-approved' },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #CCFBF1' }}>
                  <span style={{ fontSize: 13, color: '#64748B' }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#134E4A' }}>{row.value}</span>
                </div>
              ))}
            </div>

            {zone.requires_supervisor_sign && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#EDE9FE', borderRadius: 10, padding: '10px 14px', marginBottom: 20,
              }}>
                <Shield size={18} color="#7C3AED" />
                <span style={{ fontSize: 13, color: '#5B21B6', fontWeight: 600 }}>
                  This zone requires supervisor approval before marked clean.
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setPhase('photo')}>
                <ArrowLeft size={16} />
                Back
              </button>
              <button
                className="btn btn-success btn-lg"
                style={{ flex: 1 }}
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? (
                  <><div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Submitting...</>
                ) : (
                  <><Send size={20} /> Submit Session</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
